import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import type { StepperStep } from "./types";

/**
 * Report the screen the visitor just answered, with the facts it is authored
 * to keep.
 *
 * WHICH facts is the step's `SaveKeys` option, not this file's decision: the
 * endpoint only persists keys the step declared, and reading the same option
 * here means the day scoping (`Day1-Door` vs `Day2-Door`) is authored in the
 * adventure instead of hardcoded on both sides. `values` is keyed by the tail
 * of the key name, which is the part the prefix scopes — `Day2-Door` → Door,
 * `Member-Handler` → Handler.
 *
 * Per step, as each is answered. There is deliberately no summary row at the
 * end: stepper reads these rows directly, and a blob repeating what the steps
 * already said is a second copy free to disagree with the first — and missing
 * entirely for a visitor who stops halfway.
 *
 * Fire-and-forget: a failed log must never strand the visitor mid-walk.
 */
export function reportStep(
  adventureId: string | number,
  step: StepperStep,
  values: Record<string, string> = {},
) {
  const data: Record<string, string> = {};
  for (const key of String(step.options.SaveKeys ?? "").split(",")) {
    const name = key.trim();
    const value = values[name.split("-").pop() ?? ""];
    if (name && value) data[name] = value;
  }
  // The Message is the endpoint's job (it renders the facts it stored), but
  // older deployments only understand `message` and would log a blank row.
  // Same label rule as its describe_facts, so both spell the row the same.
  // ponytail: drop once every host runs the SaveKeys endpoint.
  const message = Object.entries(data)
    .map(
      ([k, v]) =>
        `${k.replace(/^(?:Day\d+-|Member-)/, "").replace(/ID$/, "")}: ${v}`,
    )
    .join("; ");
  apiClient
    .post(API_ENDPOINTS.stepper.journey(adventureId), {
      stepName: step.stepName,
      direction: "FromMember",
      data,
      message,
    })
    .catch(() => {});
}
